import React, { useState, useCallback } from 'react';
import { Button, Input, Card, Space, Typography, Switch } from 'antd';
import {
    CheckOutlined
} from '@ant-design/icons';

const { Title } = Typography;

const VirtualKeyboard = ({ onKeyPress, onClose, visible }) => {
    const [language, setLanguage] = useState('ru');
    const [shiftPressed, setShiftPressed] = useState(false);
    const [capsLock, setCapsLock] = useState(false);

    const keyboardLayouts = {
        ru: {
            row1: ['й', 'ц', 'у', 'к', 'е', 'н', 'г', 'ш', 'щ', 'з', 'х'],
            row2: ['ф', 'ы', 'в', 'а', 'п', 'р', 'о', 'л', 'д', 'ж', 'э'],
            row3: ['ё', 'я', 'ч', 'с', 'м', 'и', 'т', 'ь', 'б', 'ю', 'ъ']
        },
        en: {
            row1: ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
            row2: ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
            row3: ['z', 'x', 'c', 'v', 'b', 'n', 'm']
        }
    };

    const symbolLayouts = {
        ru: {
            row1: ['Й', 'Ц', 'У', 'К', 'Е', 'Н', 'Г', 'Ш', 'Щ', 'З', 'Х'],
            row2: ['Ф', 'Ы', 'В', 'А', 'П', 'Р', 'О', 'Л', 'Д', 'Ж', 'Э'],
            row3: ['Ё', 'Я', 'Ч', 'С', 'М', 'И', 'Т', 'Ь', 'Б', 'Ю', 'Ъ']
        },
        en: {
            row1: ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
            row2: ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
            row3: ['Z', 'X', 'C', 'V', 'B', 'N', 'M']
        }
    };

    const numpadLayout = [
        ['7', '8', '9'],
        ['4', '5', '6'],
        ['1', '2', '3'],
        ['0']
    ];

    const getCurrentLayout = () => {
        if (shiftPressed || capsLock) {
            return symbolLayouts[language];
        }
        return keyboardLayouts[language];
    };

    const handleKeyPress = useCallback((key) => {
        if (onKeyPress) {
            onKeyPress(key);
        }
    }, [onKeyPress]);

    const handleShiftPress = useCallback(() => {
        setShiftPressed(!shiftPressed);
    }, [shiftPressed]);

    const handleCapsLock = useCallback(() => {
        setCapsLock(!capsLock);
        setShiftPressed(false);
    }, [capsLock]);

    const handleLanguageSwitch = useCallback(() => {
        setLanguage(language === 'ru' ? 'en' : 'ru');
        setShiftPressed(false);
    }, [language]);

    const handleSpace = useCallback(() => {
        handleKeyPress(' ');
    }, [handleKeyPress]);

    const handleBackspace = useCallback(() => {
        if (onKeyPress) {
            onKeyPress('Backspace');
        }
    }, [onKeyPress]);

    const ShiftIcon = ({ active }) => (
        <svg viewBox="0 0 13.4511 15.217" fill="currentColor"
             width="1em"
             height="1em">
            <path
                d={active ?
                    "M6.54098 0C6.26175 0 5.9246 0.121833 5.67932 0.384696L0.548085 5.85587C0.166183 6.27293 0 6.60529 0 7.02754C0 7.63751 0.458576 8.10808 1.06855 8.10808L3.49213 8.10808L3.49213 13.3849C3.49213 14.537 4.15086 15.2138 5.29098 15.2138L7.77181 15.2138C8.90593 15.2138 9.57346 14.537 9.57346 13.3849L9.57346 8.10808L11.9774 8.10808C12.5874 8.10808 13.082 7.63751 13.082 7.01555C13.082 6.60448 12.9182 6.31687 12.4979 5.85587L7.40583 0.381902C7.15456 0.119039 6.83458 0 6.54098 0Z"
                    : "M6.54098 0.00319934C6.26175 0.00319934 5.9246 0.125033 5.67932 0.387896L0.548085 5.85907C0.166183 6.27613 0 6.60849 0 7.03074C0 7.64071 0.458576 8.11128 1.06855 8.11128L3.37224 8.11128L3.37224 13.2183C3.37224 14.4759 4.10129 15.217 5.34088 15.217L7.71591 15.217C8.9583 15.217 9.68735 14.4759 9.68735 13.2183L9.68735 8.11128L11.9774 8.11128C12.5874 8.11128 13.082 7.64071 13.082 7.01875C13.082 6.60768 12.9182 6.31408 12.4979 5.85907L7.40583 0.385101C7.15456 0.122238 6.83458 0.00319934 6.54098 0.00319934ZM6.54098 1.8798C6.57293 1.8798 6.59331 1.89138 6.62806 1.92613L10.9305 6.41193C10.9569 6.4411 10.9717 6.47625 10.9717 6.51141C10.9717 6.56973 10.9309 6.61927 10.867 6.61927L8.41433 6.61927C8.25892 6.61927 8.18541 6.68679 8.18541 6.84819L8.18541 13.0946C8.18541 13.4941 7.97565 13.709 7.56775 13.709L5.49464 13.709C5.08674 13.709 4.88017 13.4941 4.88017 13.0946L4.88017 6.84819C4.88017 6.68679 4.80666 6.61927 4.64526 6.61927L2.26012 6.61927C2.1962 6.61927 2.15266 6.57573 2.15266 6.51141C2.15266 6.47625 2.17024 6.4411 2.1994 6.41193L6.45389 1.92613C6.48865 1.89138 6.51181 1.8798 6.54098 1.8798Z"
                }
                fill="currentColor"
                fillOpacity="0.85"
            />
        </svg>
    );

    const LanguageIcon = () => (
        <svg viewBox="0 0 14.8569 14.4906" fill="currentColor"
             width="1em"
             height="1em">
            <path d="M7.2409 13.7205C9.24319 13.7205 10.8296 10.882 10.8296 7.25848C10.8296 3.61414 9.24918 0.766856 7.2409 0.766856C5.23262 0.766856 3.6582 3.61414 3.6582 7.25848C3.6582 10.882 5.23861 13.7205 7.2409 13.7205ZM7.2409 1.80033C8.52811 1.80033 9.71982 4.39674 9.71982 7.25848C9.71982 10.0907 8.52811 12.6871 7.2409 12.6871C5.95968 12.6871 4.76798 10.0962 4.76798 7.25848C4.76798 4.39674 5.95968 1.80033 7.2409 1.80033ZM6.6948 0.860741L6.6948 13.5915L7.787 13.5915L7.787 0.860741ZM7.2409 9.43475C5.04926 9.43475 3.08014 9.99765 2.14729 10.8981L2.97982 11.5873C3.84277 10.9141 5.39759 10.5269 7.2409 10.5269C9.09021 10.5269 10.639 10.9141 11.508 11.5873L12.3405 10.8981C11.4017 9.99765 9.43853 9.43475 7.2409 9.43475ZM13.4936 6.6948L0.988237 6.6948L0.988237 7.787L13.4936 7.787ZM7.2409 5.08221C9.43853 5.08221 11.4017 4.51931 12.3405 3.61882L11.508 2.9297C10.639 3.60289 9.09021 3.99002 7.2409 3.99002C5.40038 3.99002 3.84836 3.60289 2.97982 2.9297L2.14729 3.61882C3.08014 4.51931 5.04926 5.08221 7.2409 5.08221ZM7.2409 14.4818C11.2419 14.4818 14.4878 11.2419 14.4878 7.2409C14.4878 3.23993 11.2419 0 7.2409 0C3.24592 0 0 3.23993 0 7.2409C0 11.2419 3.24592 14.4818 7.2409 14.4818ZM7.2409 13.1476C3.98251 13.1476 1.34017 10.4993 1.34017 7.2409C1.34017 3.98251 3.98251 1.33417 7.2409 1.33417C10.4993 1.33417 13.1476 3.98251 13.1476 7.2409C13.1476 10.4993 10.4993 13.1476 7.2409 13.1476Z" fill="currentColor" fillOpacity="0.85"/>
        </svg>
    );

    const BackspaceIcon = () => (
        <svg viewBox="0 0 22 22" fill="currentColor"
             width="1em"
             height="1em">
            <path fillRule="evenodd" clipRule="evenodd" fill="currentColor" d="M20.9886 6.15385C20.9886 4.96923 19.9601 4 18.7032 4H8.69319C7.34481 4 6.07642 4.55998 5.20797 5.52921L1.53992 9.61076C0.820026 10.4185 0.820026 11.5923 1.53992 12.3892L5.20797 16.4708C6.07642 17.44 7.35623 18 8.69319 18H18.7146C19.9716 18 21 17.0308 21 15.8462V6.15385H20.9886ZM17.092 13.0569C17.4691 13.4123 17.4691 13.9723 17.092 14.3277C16.9092 14.5 16.6578 14.5861 16.4178 14.5861C16.1778 14.5861 15.9264 14.5 15.7436 14.3277L13.5611 12.2708L11.3785 14.3277C11.1957 14.5 10.9443 14.5861 10.7043 14.5861C10.4644 14.5861 10.213 14.5 10.0301 14.3277C9.65305 13.9723 9.65305 13.4123 10.0301 13.0569L12.2127 11L10.0301 8.94307C9.65305 8.58769 9.65305 8.0277 10.0301 7.67231C10.3958 7.31693 11.0014 7.31693 11.3785 7.67231L13.5611 9.72924L15.7436 7.67231C16.1093 7.31693 16.7149 7.31693 17.092 7.67231C17.4691 8.0277 17.4691 8.58769 17.092 8.94307L14.9094 11L17.092 13.0569Z"/>
        </svg>
    );

    const currentLayout = getCurrentLayout();

    if (!visible) return null;

    return (
        <div
            style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                width: '100%',
                backgroundColor: '#1F2937',
                padding: '20px',
                zIndex: 1005,
                userSelect: 'none',
                borderTopLeftRadius: '30px',
                borderTopRightRadius: '30px'
            }}
        >
            <div style={{ display: 'flex', gap: '16px' }}>
                {/* Основная клавиатура */}
                <div style={{ flex: 1 }}>
                    {/* Первая строка */}
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                        {currentLayout.row1.map((key, index) => (
                            <Button
                                key={index}
                                onClick={() => handleKeyPress(key)}
                                style={{
                                    flex: 1,
                                    height: '68px',
                                    fontSize: '20px',
                                    fontWeight: '500',
                                    backgroundColor: '#374151',
                                    color: 'white',
                                    border: 'none'
                                }}
                            >
                                {key}
                            </Button>
                        ))}
                    </div>

                    {/* Вторая строка */}
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                        <div style={{ width: '28px' }}></div>
                        {currentLayout.row2.map((key, index) => (
                            <Button
                                key={index}
                                onClick={() => handleKeyPress(key)}
                                style={{
                                    flex: 1,
                                    height: '68px',
                                    fontSize: '20px',
                                    fontWeight: '500',
                                    backgroundColor: '#374151',
                                    color: 'white',
                                    border: 'none'
                                }}
                            >
                                {key}
                            </Button>
                        ))}
                        <div style={{ width: '28px' }}></div>
                    </div>

                    {/* Третья строка с Shift */}
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                        <Button
                            onClick={handleShiftPress}
                            type={shiftPressed || capsLock ? "primary" : "default"}
                            style={{
                                width: '80px',
                                height: '68px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: shiftPressed || capsLock ? '#52c41a' : '#374151',
                                color: 'white',
                                border: 'none'
                            }}
                        >
                            <ShiftIcon active={shiftPressed || capsLock} />
                        </Button>

                        {currentLayout.row3.map((key, index) => (
                            <Button
                                key={index}
                                onClick={() => handleKeyPress(key)}
                                style={{
                                    flex: 1,
                                    height: '68px',
                                    fontSize: '20px',
                                    fontWeight: '500',
                                    backgroundColor: '#374151',
                                    color: 'white',
                                    border: 'none'
                                }}
                            >
                                {key}
                            </Button>
                        ))}

                        <Button
                            onClick={handleBackspace}
                            style={{
                                width: '80px',
                                height: '68px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#374151',
                                color: 'white',
                                border: 'none'
                            }}
                        >
                            <BackspaceIcon />
                        </Button>
                    </div>

                    {/* Нижняя строка */}
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <Button
                            onClick={handleLanguageSwitch}
                            style={{
                                width: '68px',
                                height: '68px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#374151',
                                color: 'white',
                                border: 'none'
                            }}
                            title={language === 'ru' ? 'Переключить на EN' : 'Переключить на RU'}
                        >
                            <LanguageIcon />
                        </Button>

                        <Button
                            onClick={() => handleKeyPress(',')}
                            style={{
                                width: '68px',
                                height: '68px',
                                fontSize: '20px',
                                fontWeight: '500',
                                backgroundColor: '#374151',
                                color: 'white',
                                border: 'none'
                            }}
                        >
                            ,
                        </Button>

                        <Button
                            onClick={handleSpace}
                            style={{
                                flex: 1,
                                height: '68px',
                                fontSize: '18px',
                                fontWeight: '500',
                                backgroundColor: '#374151',
                                color: 'white',
                                border: 'none'
                            }}
                        >
                            Пробел
                        </Button>

                        <Button
                            onClick={() => handleKeyPress('.')}
                            style={{
                                width: '68px',
                                height: '68px',
                                fontSize: '20px',
                                fontWeight: '500',
                                backgroundColor: '#374151',
                                color: 'white',
                                border: 'none'
                            }}
                        >
                            .
                        </Button>

                        {onClose && (
                            <Button
                                onClick={onClose}
                                type="primary"
                                style={{
                                    width: '68px',
                                    height: '68px',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#52c41a',
                                    borderColor: '#52c41a'
                                }}
                                title="Закрыть клавиатуру"
                            >
                                <CheckOutlined />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Numpad */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        borderLeft: '1px solid #374151',
                        paddingLeft: '16px',
                        minWidth: '250px'
                    }}
                >
                    {numpadLayout.map((row, rowIndex) => (
                        <div key={rowIndex} style={{ display: 'flex', gap: '6px' }}>
                            {row.map((key, index) => (
                                <Button
                                    key={index}
                                    onClick={() => handleKeyPress(key)}
                                    style={{
                                        flex: 1,
                                        height: '68px',
                                        fontSize: '20px',
                                        fontWeight: '500',
                                        backgroundColor: '#374151',
                                        color: 'white',
                                        border: 'none'
                                    }}
                                >
                                    {key}
                                </Button>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
export default VirtualKeyboard;